import React, { lazy, Suspense } from "react";
import { SEOHead } from "@/components/SEOHead";
import Navbar from "@/components/Navbar";
import Pricing from "@/components/Pricing";
import CoHostComparison from "@/components/CoHostComparison";
import { LazySection } from "@/components/LazySection";
import { CheckCircle2, ArrowRight, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";

const Footer = lazy(() => import("@/components/Footer"));

const PricingPage = () => {
  const scrollToForm = () => {
    window.location.href = "/#lead-form";
  };

  return (
    <div className="min-h-screen bg-background font-body">
      <SEOHead
        title="Airbnb Management Pricing & Tiers | JNN Homes"
        description="Transparent, performance-based pricing for VA & DC Airbnb management. From 15% to 25% — choose the exact level of service you need."
        canonical="https://www.jnnhomes.com/pricing"
        schemaJson={{
          "@context": "https://schema.org",
          "@type": "PriceSpecification",
          name: "JNN Homes Airbnb Management Pricing",
          description:
            "Performance-based management fees from 15% to 25% of booking revenue, with no setup fees and no lock-in contracts.",
          url: "https://www.jnnhomes.com/pricing",
          provider: {
            "@type": "LocalBusiness",
            name: "JNN Homes",
            url: "https://www.jnnhomes.com",
          },
        }}
      />
      <Navbar />

      {/* Hero */}
      <section className="relative pt-32 pb-16 lg:pt-40 lg:pb-20 bg-gradient-to-br from-background via-background to-muted/50 overflow-hidden">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[400px] opacity-20 pointer-events-none bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/40 via-transparent to-transparent" />
        <div className="container relative mx-auto px-4 text-center max-w-4xl">
          <p className="text-primary font-semibold uppercase tracking-widest text-sm mb-4">
            Transparent Pricing
          </p>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-heading font-bold text-foreground leading-tight mb-6">
            No Hidden Fees.
            <br />
            <span className="text-primary italic">Just Results.</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
            We earn more when you earn more. Our percentage-based model means
            our goals are perfectly aligned with yours.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={scrollToForm}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-lg px-8 py-6 h-auto rounded-full"
            >
              Get a Free Revenue Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <a href="tel:7038905425">
              <Button
                variant="outline"
                className="font-bold text-lg px-8 py-6 h-auto rounded-full w-full"
              >
                <Phone className="w-5 h-5 mr-2" />
                (703) 890-5425
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* What's Included Banner */}
      <section className="py-10 bg-muted/20 border-y border-border">
        <div className="container mx-auto px-4">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
            {[
              "No setup fees or hidden charges",
              "Cancel anytime — no lock-in contracts",
              "Performance-aligned fee structure",
              "Free initial revenue analysis",
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3">
                <CheckCircle2 className="w-5 h-5 text-primary shrink-0" />
                <span className="text-sm font-medium text-foreground">
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Tiers */}
      <Pricing />

      <div className="bg-muted/20 border-t border-border/60 py-4">
        <p className="text-center text-[0.7rem] sm:text-xs text-muted-foreground leading-relaxed max-w-2xl mx-auto px-4">
          Offer for new Washington DC and Manassas owner clients only, limited
          to the first 10 homes signed. After 3 months, standard plan pricing
          (15%, 20% or 25%) applies.
        </p>
      </div>

      {/* Comparison Table */}
      <CoHostComparison />

      {/* FAQ callout */}
      <section className="py-16 bg-background border-t border-border">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <h2 className="text-2xl font-bold mb-4">Still Have Questions?</h2>
          <p className="text-muted-foreground mb-6">
            We're happy to walk you through exactly what's included in each tier
            and which plan fits your property best.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              onClick={scrollToForm}
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold px-8 py-5 h-auto rounded-full"
            >
              Get Free Income Estimate
            </Button>
            <a href="tel:7038905425">
              <Button
                variant="outline"
                className="font-bold px-8 py-5 h-auto rounded-full w-full sm:w-auto"
              >
                <Phone className="w-4 h-4 mr-2" />
                Call Us Now
              </Button>
            </a>
          </div>
        </div>
      </section>

      <LazySection
        minHeight="160px"
        rootMargin="200px"
        fallback={<div className="h-40 bg-card/20" />}
      >
        <Suspense fallback={<div className="h-40 bg-card/20" />}>
          <Footer />
        </Suspense>
      </LazySection>
    </div>
  );
};

export default PricingPage;
